/**
 * Lokale Datums-Helfer für den Kalender.
 *
 * Hintergrund (Batch-13, Kalender-Seitenleiste): `toISOString()` liefert
 * UTC. Die lokale Mitternacht des 25.09. (Europe/Berlin, +02:00) ist in UTC
 * noch der 24.09. um 22:00 — ein Tagesvergleich über `toISOString().slice(0,10)`
 * ordnet Termine deshalb dem Vortag zu, und ein „Neuer Termin" nach Tagesklick
 * startete mit `24.09. 22:00`. Alle Tagesschlüssel und Formular-Startwerte
 * müssen aus den LOKALEN Datumsbestandteilen gebaut werden.
 */

const pad = (n: number) => String(n).padStart(2, '0');

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * `YYYY-MM-DD` in lokaler Zeit.
 *
 * - `Date` → Jahr/Monat/Tag der lokalen Zeit.
 * - reines Datum (`2026-09-25`, z.B. Ganztagstermine ohne Uhrzeit) → unverändert,
 *   NICHT über `new Date()` (das wäre UTC-Mitternacht und in Berlin 02:00).
 * - Datetime-String (mit oder ohne Offset) → geparst und lokal ausgegeben,
 *   damit der Schlüssel zu dem passt, was FullCalendar bei einem Tagesklick
 *   als lokale Mitternacht liefert.
 */
export function toLocalDateKey(value: Date | string | null | undefined): string {
  if (!value) return '';
  if (typeof value === 'string') {
    if (DATE_ONLY.test(value)) return value;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value.slice(0, 10);
    return toLocalDateKey(d);
  }
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

/** `Date` → Wert für `<input type="datetime-local">` (`YYYY-MM-DDTHH:mm`) in lokaler Zeit. */
export function toLocalDatetimeInput(date: Date): string {
  return `${toLocalDateKey(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const BERLIN_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit',
});

/**
 * Kalendertag (`YYYY-MM-DD`) in Europe/Berlin, unabhängig von der Zeitzone des
 * Browsers.
 *
 * - reines Datum → unverändert.
 * - Datetime (z. B. v1-Liste `"2026-10-29T23:00:00.000000Z"`, UTC-Mitternacht
 *   des Berliner 30.10.) → Berliner Tag. Ein `slice(0, 10)` lieferte hier den
 *   Vortag.
 * - nicht parsbar → leerer String.
 */
export function toBerlinDateKey(value: Date | string | null | undefined): string {
  if (!value) return '';
  if (typeof value === 'string') {
    const s = value.trim();
    if (DATE_ONLY.test(s)) return s;
    // Datetime ohne Offset („2026-10-30 00:00:00"): Wandzeit, Tag direkt übernehmen.
    const wall = s.match(/^(\d{4}-\d{2}-\d{2})[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/);
    if (wall) return wall[1];
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return BERLIN_DAY.format(d);
}
